import { QuestionConfig, FormConfig } from '../types/FeedbackForm';
import { translate } from '../i18n/translate';
import { TxKeyPath } from '../i18n/i18n';

export interface ValidationResult {
  isValid: boolean;
  errors: {
    [key: string]: string;
  };
}

/**
 * Email validation regex pattern
 * Matches standard email format: user@domain.com
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates if a string is a valid email address
 * @param email - The email string to validate
 * @returns true if the email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  return EMAIL_REGEX.test(email.trim());
};

/**
 * Checks if a value is required (not empty after trimming)
 * @param value - The value to check (string, null, or undefined)
 * @returns true if the value is not empty, false otherwise
 * 
 * @example
 * ```ts
 * if (!isRequired(formData.name)) {
 *   errors.name = 'Name is required';
 * }
 * ```
 */
export const isRequired = (value: string | null | undefined): boolean => {
  return value?.trim() !== '';
};

/**
 * Validates if a required field has a value
 * @param value - The value to validate (string, null, or undefined)
 * @param fieldName - The name of the field for the error message
 * @returns Error message string if invalid, null if valid
 * 
 * @example
 * ```ts
 * const nameError = validateRequired(formData.name, 'Name');
 * if (nameError) errors.name = nameError;
 * ```
 */
export const validateRequired = (value: string | null | undefined, fieldName: string): string | null => {
  return isRequired(value) ? null : `${fieldName} is required`;
};

export const validateFormResponse = (
  questions: QuestionConfig[],
  responses: { [key: string]: any }
): ValidationResult => {
  const errors: { [key: string]: string } = {};

  questions.forEach((question, index) => {
    const response = responses[`question-${index}`];

    // Check required fields
    if (question.required && !response) {
      errors[`question-${index}`] = translate('common.validation.questionRequired');
      return;
    }

    // Validate based on question type
    switch (question.type) {
      case 'triad':
        if (response && (!response.x || !response.y)) {
          errors[`question-${index}`] = translate('common.validation.selectTriadPosition');
        }
        break;

      case 'multiple_choice':
        if (response) {
          const config = question as any; // TODO: Fix type
          if (config.allowMultiple) {
            if (!Array.isArray(response) || response.length === 0) {
              errors[`question-${index}`] = translate('common.validation.selectAtLeastOne');
            }
          } else if (!response) {
            errors[`question-${index}`] = translate('common.validation.selectOption');
          }
        }
        break;

      case 'rating':
        if (response) {
          const config = question as any; // TODO: Fix type
          const value = Number(response);
          if (isNaN(value) || value < config.min || value > config.max) {
            errors[`question-${index}`] = translate('common.validation.valueBetween', {
              min: String(config.min),
              max: String(config.max)
            });
          }
        }
        break;

      case 'text':
        if (response) {
          const config = question as any; // TODO: Fix type
          if (config.maxLength && response.length > config.maxLength) {
            errors[`question-${index}`] = translate('common.validation.textTooLong', {
              maxLength: String(config.maxLength)
            });
          }
        }
        break;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateFormConfig = (config: FormConfig): ValidationResult => {
  const errors: { [key: string]: string } = {};

  if (!config.title.trim()) {
    errors.title = translate('formBuilder.validation.formTitleRequired' as TxKeyPath);
  }

  if (!config.submitButtonText?.trim()) {
    errors.submitButtonText = translate('formBuilder.validation.submitButtonTextRequired' as TxKeyPath);
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateQuestion = (question: QuestionConfig): ValidationResult => {
  const errors: { [key: string]: string } = {};

  if (!question.title.trim()) {
    errors.title = translate('formBuilder.validation.questionTitleRequired' as TxKeyPath);
  }

  switch (question.type) {
    case 'multiple_choice': {
      const mcConfig = question as any; // TODO: Fix type
      if (!mcConfig.options || mcConfig.options.length < 2) {
        errors.options = translate('formBuilder.validation.atLeastTwoOptions' as TxKeyPath);
      } else if (mcConfig.options.some((opt: string) => !opt.trim())) {
        errors.options = translate('formBuilder.validation.allOptionsMustHaveContent' as TxKeyPath);
      }
      break;
    }

    case 'rating': {
      const rConfig = question as any; // TODO: Fix type
      if (rConfig.min >= rConfig.max) {
        errors.range = translate('formBuilder.validation.maxMustBeGreaterThanMin' as TxKeyPath);
      }
      if (!rConfig.labels?.min || !rConfig.labels?.max) {
        errors.labels = translate('formBuilder.validation.bothLabelsRequired' as TxKeyPath);
      }
      break;
    }

    case 'triad': {
      const tConfig = question as any; // TODO: Fix type
      if (!tConfig.pointA || !tConfig.pointB || !tConfig.pointC) {
        errors.points = translate('formBuilder.validation.allThreePointsRequired' as TxKeyPath);
      }
      break;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}; 